import { useEffect, useMemo, useState } from 'react';

import { useFilterQuery } from '@bringg-frontend/hooks';
import { FilterBar2ListItem, convertFilterObjectValuesToRelevantValueTypes } from '@bringg/react-components';
import { useTranslation } from 'react-i18next';

import { useStores } from 'bringg-web/recipes';
import { FieldsInitialValues } from '../monitoring-filter-bar';
import { FilterBarListItemKeys, FiltersObject } from '../types';
import useFilterBarItems from './use-filter-bar-items';
import { userFiltersProvider } from '../utils/user-filters-provider';
import { monitoringFiltersProvider } from '../utils/monitoring-filter-provider';
import notification from 'bringg-web/services/notification';

const normalizeTeamsFilter = (selectedTeams?: number | number[]) => {
	const normalized = typeof selectedTeams === 'number' ? [selectedTeams] : selectedTeams;

	if (normalized) {
		return { selectedTeams: normalized };
	}

	return {};
};

const normalizeQueryFilters = (filterObject: FiltersObject, filterBarFilters: FilterBar2ListItem[]) => {
	const { selectedTeams, ...restOfFilters } = filterObject;

	return {
		...normalizeTeamsFilter(selectedTeams),
		...(convertFilterObjectValuesToRelevantValueTypes(filterBarFilters, restOfFilters) as FiltersObject)
	};
};

const useFiltersManager = (storageKey: string) => {
	const { t } = useTranslation();
	const { runStore, driversStore } = useStores();
	const [fieldsInitialValues, setFieldsInitialValues] = useState<FieldsInitialValues>({});

	const {
		filterObject,
		initialFilterObjectFromQuery,
		setFilterObject: setFilterObjectForQuery
	} = useFilterQuery<FiltersObject>({
		resetUntrackedParamsOnChange: true
	});

	const filterBarFilters = useFilterBarItems();

	const initialValues = useMemo(() => {
		if (!filterBarFilters.length) return {};

		const valuesFromQuery = normalizeQueryFilters(initialFilterObjectFromQuery, filterBarFilters);
		const valuesFromStorage = userFiltersProvider.getUserMonitoringFilters(storageKey);

		return monitoringFiltersProvider.cleanFilterEntries(
			Object.keys(valuesFromQuery).length ? valuesFromQuery : valuesFromStorage
		);
	}, [initialFilterObjectFromQuery, filterBarFilters, storageKey]);

	const { initialFilterBarMappedValues, initialAdditional } = useMemo(() => {
		const mappedValues = monitoringFiltersProvider.mapQueryValuesToFilterBar(initialValues, filterBarFilters);

		const additionalValues = Object.keys(mappedValues).filter(key =>
			filterBarFilters.find(filter => mappedValues[key] !== undefined && filter.additional && filter.name === key)
		);

		return {
			initialFilterBarMappedValues: mappedValues,
			initialAdditional: additionalValues as FilterBarListItemKeys[]
		};
	}, [initialValues]);

	useEffect(() => {
		const initFilters = async () => {
			try {
				const { selectedRuns, selectedUsers } = initialValues;

				const [runs, driversByIds] = await Promise.all([
					runStore.loadAllByIds(selectedRuns || []),
					driversStore.loadMany(selectedUsers || [])
				]);

				setFieldsInitialValues({
					selectedRuns: runs.map(({ external_id, id }) => ({
						label: external_id,
						value: id
					})),
					selectedUsers: driversByIds.map(driver => ({
						label: driver?.name,
						value: driver?.id
					}))
				});
			} catch (error) {
				console.error('Monitoring filter bar - failed while fetching data: ', error);
				notification.error(t('ROUTE_MONITORING.FILTERS.ERROR_FETCHING_DATA'));
			}
		};

		if (!Object.keys(fieldsInitialValues).length) {
			initFilters();
		}
	}, [fieldsInitialValues, initialValues]);

	const filterBarFiltersWithValues = useMemo(() => {
		if (Object.keys(fieldsInitialValues).length) {
			return filterBarFilters.map(filterItem => {
				if (fieldsInitialValues[filterItem.name]) {
					filterItem.values = fieldsInitialValues[filterItem.name];
				}

				return filterItem;
			});
		}

		return filterBarFilters;
	}, [fieldsInitialValues, filterBarFilters]);

	const setFilterObject = (filterObject: FiltersObject) => {
		userFiltersProvider.saveUserMonitoringFilters(storageKey, filterObject);
		setFilterObjectForQuery(filterObject);
	};

	return {
		filterBarFiltersWithValues,
		filterObject: normalizeQueryFilters(filterObject, filterBarFilters),
		initialValues,
		initialFilterBarMappedValues,
		initialAdditional,
		setFilterObject
	};
};

export default useFiltersManager;
